// The card's job and epic controls as calls and answers (#6753, epic #6552): the pause and
// resume doors #6751 serves (`POST /v1/tasks/:id/pause|resume`,
// `POST /v1/runners/:id/pause-jobs|resume-jobs`, `POST /v1/plans/:planId/pause|resume`), the
// epic model door #7012 serves (`PUT /v1/plans/:planId/model`), and how a refusal reads.
// Stop rides the existing task and plan cancels (`cancelTask`, `cancelPlan`).
//
// A REFUSAL IS TRANSLATED BY ITS CODE, NEVER SHOWN AS THE SERVER'S `detail`. The doors'
// sentences are written for MCP callers (`PAUSE_REFUSAL_COPY`, `planModelRefusalSentence`):
// they carry backticks and MCP verbs (`task cancel`, `box update`). The card reads the
// problem's `reason` (`ApiError.extensions.reason`) on the pause, resume and model doors, and
// the STATUS on the task cancel route, whose 409 `not_cancellable_review` is a plain
// `conflict` problem with no `reason` member. A code this bundle does not know reads the
// fallback "This change was refused. Nothing was changed." This is the second exception to
// `apps/dashboard/CLAUDE.md` § Boundary's "the refusal shown is the API's own `detail`",
// recorded there. The one server sentence the card repeats is `planStopMessage`'s
// `skipped_reason` on an epic Stop, as the Epic page does.
//
// A 429 is read from `ApiError.extensions.retry_after`, never the `Retry-After` header:
// `apiFetch` exposes no headers (`./api-errors.ts`). Only the six pause doors have a server
// flood bound (#6751, `pause-rate-limited`); the task cancel, the plan cancel and the plan
// model write have none, so the page's flood guard (`./flood-guard.ts`) is their only one.

import type { TaskLlmPreference } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import { ApiError, apiFetch } from './api';
import { FLOOD_LOCK_MS } from './flood-guard';
import { SESSION_TOKEN } from './session';

// ─── The doors ───────────────────────────────────────────────────────────────

/** `POST /v1/tasks/:id/pause`: 202 `pause_requested`, 200 `paused` / `already_paused`. */
export type TaskPauseAnswer = {
  readonly task_id: string;
  readonly kind: 'pause_requested' | 'paused' | 'already_paused';
};

/** `POST /v1/tasks/:id/resume`: 200 `resumed` / `pause_withdrawn` / `already_resumed`. */
export type TaskResumeAnswer = {
  readonly task_id: string;
  readonly kind: 'resumed' | 'pause_withdrawn' | 'already_resumed';
  readonly pinned_runner_id?: string | null;
};

/** A job a batch door left as it was, and the task door's code for why. */
export type SkippedJob = { readonly task_id: string; readonly reason: string };

/** `POST /v1/runners/:id/pause-jobs|resume-jobs`: each list is task ids. */
export type MachineJobsAnswer = {
  readonly box_id: string;
  readonly pause_requested: readonly string[];
  readonly paused: readonly string[];
  readonly resumed: readonly string[];
  readonly pause_withdrawn: readonly string[];
  readonly skipped: readonly SkippedJob[];
  readonly next_step?: string;
};

/** `POST /v1/plans/:planId/pause`. `held` is the waiting slices; `skipped` the refused running ones. */
export type PlanPauseAnswer = {
  readonly plan_id: string;
  readonly paused_at: string;
  readonly already_paused: boolean;
  readonly pause_requested: readonly string[];
  readonly paused: readonly string[];
  readonly held: readonly string[];
  readonly skipped: readonly SkippedJob[];
};

/** `POST /v1/plans/:planId/resume`. */
export type PlanResumeAnswer = {
  readonly plan_id: string;
  readonly already_resumed: boolean;
  readonly resumed: readonly string[];
  readonly pause_withdrawn: readonly string[];
  readonly skipped: readonly SkippedJob[];
  readonly next_step?: string;
};

/** `PUT /v1/plans/:planId/model`. */
export type PlanModelAnswer = {
  readonly plan_id: string;
  readonly llm: TaskLlmPreference | null;
  readonly previous_llm: TaskLlmPreference | null;
};

const enc = encodeURIComponent;
const post = { method: 'POST' } as const;

export function pauseTask(taskId: string, token: string | null = SESSION_TOKEN) {
  return apiFetch<TaskPauseAnswer>(`/v1/tasks/${enc(taskId)}/pause`, token, post);
}

/** `anywhere: true` lets any eligible machine continue from the saved work. */
export function resumeTask(
  taskId: string,
  anywhere: boolean,
  token: string | null = SESSION_TOKEN,
) {
  return apiFetch<TaskResumeAnswer>(`/v1/tasks/${enc(taskId)}/resume`, token, {
    method: 'POST',
    body: JSON.stringify(anywhere ? { anywhere: true } : {}),
  });
}

export function pauseMachineJobs(boxId: string, token: string | null = SESSION_TOKEN) {
  return apiFetch<MachineJobsAnswer>(`/v1/runners/${enc(boxId)}/pause-jobs`, token, post);
}

export function resumeMachineJobs(boxId: string, token: string | null = SESSION_TOKEN) {
  return apiFetch<MachineJobsAnswer>(`/v1/runners/${enc(boxId)}/resume-jobs`, token, post);
}

export function pausePlan(planId: string, token: string | null = SESSION_TOKEN) {
  return apiFetch<PlanPauseAnswer>(`/v1/plans/${enc(planId)}/pause`, token, post);
}

export function resumePlan(planId: string, token: string | null = SESSION_TOKEN) {
  return apiFetch<PlanResumeAnswer>(`/v1/plans/${enc(planId)}/resume`, token, post);
}

/** `null` is Automatic. */
export function setPlanModel(
  planId: string,
  llm: TaskLlmPreference | null,
  token: string | null = SESSION_TOKEN,
) {
  return apiFetch<PlanModelAnswer>(`/v1/plans/${enc(planId)}/model`, token, {
    method: 'PUT',
    body: JSON.stringify({ llm }),
  });
}

// ─── Refusals ────────────────────────────────────────────────────────────────

/**
 * Every code a door this card calls answers, each with its own sentence. The pause doors'
 * ten (`PauseRefusal`, `@developerz/mcp`, which the dashboard may not import), the model
 * door's three, and the task cancel's review carve-out.
 */
export const REFUSAL_KEY: Readonly<Record<string, string>> = {
  lane_not_pausable: 'fleet.machines.ctl_refusal_lane_not_pausable',
  task_not_running: 'fleet.machines.ctl_refusal_task_not_running',
  task_stopping: 'fleet.machines.ctl_refusal_task_stopping',
  task_moved: 'fleet.machines.ctl_refusal_task_moved',
  presence_unknown: 'fleet.machines.ctl_refusal_presence_unknown',
  machine_offline: 'fleet.machines.ctl_refusal_machine_offline',
  machine_cannot_pause: 'fleet.machines.ctl_refusal_machine_cannot_pause',
  machine_gone: 'fleet.machines.ctl_refusal_machine_gone',
  task_not_paused: 'fleet.machines.ctl_refusal_task_not_paused',
  plan_not_open: 'fleet.machines.ctl_refusal_plan_not_open',
  llm_names_nothing: 'fleet.machines.ctl_refusal_llm_names_nothing',
  unknown_key_label: 'fleet.machines.ctl_refusal_unknown_key_label',
  key_not_usable: 'fleet.machines.ctl_refusal_key_not_usable',
  not_cancellable_review: 'fleet.machines.ctl_refusal_not_cancellable_review',
};
export const REFUSAL_FALLBACK_KEY = 'fleet.machines.ctl_refusal_fallback';

/** The three reasons a RESUME lifts by sending `{anywhere: true}` (`RESUME_ANYWHERE_NEXT_STEP`). */
export const ANYWHERE_REASONS: ReadonlySet<string> = new Set([
  'machine_gone',
  'machine_offline',
  'presence_unknown',
]);

/** The translated sentence for a door's code; an unknown code reads the fallback. */
export function refusalText(code: string | null, tr: Translator): string {
  const key = code !== null && Object.hasOwn(REFUSAL_KEY, code) ? REFUSAL_KEY[code] : undefined;
  return tr(key ?? REFUSAL_FALLBACK_KEY);
}

/** Which door answered: the model and cancel doors read some statuses their own way. */
export type Door = 'pause' | 'resume' | 'jobs' | 'model' | 'task_cancel' | 'plan_cancel';

/** A door's non-2xx, as the card acts on it. */
export type DoorFailure =
  /** A refusal: its translated sentence, and whether "Resume on any machine" lifts it. */
  | { readonly kind: 'refused'; readonly code: string | null; readonly anywhere: boolean }
  /** A 429: lock the page for this many seconds. */
  | { readonly kind: 'rate_limited'; readonly seconds: number }
  /** A 404: the job, machine or epic left this account's view; re-read. */
  | { readonly kind: 'gone' }
  /** Anything else (a 5xx, the network): nothing is known to have changed. */
  | { readonly kind: 'failed' };

const reasonOf = (err: ApiError): string | null => {
  const r = err.extensions?.['reason'];
  return typeof r === 'string' && r !== '' ? r : null;
};

/** The wait a 429 names; a missing or unusable one locks for the guard's own 15 s. */
const retryAfterOf = (err: ApiError): number => {
  const s = err.extensions?.['retry_after'];
  return typeof s === 'number' && Number.isFinite(s) && s > 0 ? s : FLOOD_LOCK_MS / 1_000;
};

/** What a thrown door call means for the card. Never reads `detail`. */
export function readFailure(err: unknown, door: Door): DoorFailure {
  if (!(err instanceof ApiError)) return { kind: 'failed' };
  if (err.status === 429) return { kind: 'rate_limited', seconds: retryAfterOf(err) };
  if (err.status === 404) return { kind: 'gone' };
  if (door === 'task_cancel' && err.status === 409) {
    return { kind: 'refused', code: 'not_cancellable_review', anywhere: false };
  }
  if (err.status === 409 || err.status === 400 || err.status === 422) {
    const code = reasonOf(err);
    return {
      kind: 'refused',
      code,
      anywhere: door === 'resume' && code !== null && ANYWHERE_REASONS.has(code),
    };
  }
  return { kind: 'failed' };
}
